import { useAuth, useSignIn, useUser } from "@clerk/react";
import { isClerkAPIResponseError } from "@clerk/react/errors";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AuthScreen } from "@/components/auth/auth-screen";
import { ConfirmNote } from "@/components/auth/confirm-note";
import { InlineError } from "@/components/auth/inline-error";
import { PasswordField } from "@/components/auth/password-field";
import { PrimaryButton } from "@/components/auth/primary-button";
import { StrengthGauge } from "@/components/auth/strength-gauge";
import { SuccessState } from "@/components/auth/success-state";
import { authPath, redirectAfterAuth } from "@/lib/auth-helpers";
import { clerkFailureMessage } from "@/lib/clerk-errors";
import { confirmState } from "@/lib/confirm-password";
import { followDecorated } from "@/lib/follow-auth";

const redirectDelay = (): number =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 400 : 1100;

const scheduleStudio = (decorateUrl?: (url: string) => string) => {
  window.setTimeout(() => {
    if (decorateUrl) {
      followDecorated(decorateUrl);
      return;
    }
    window.location.assign(redirectAfterAuth());
  }, redirectDelay());
};

const saveResetPassword = async ({
  password,
  signIn,
  signOutOthers,
}: {
  password: string;
  signIn: ReturnType<typeof useSignIn>["signIn"];
  signOutOthers: boolean;
}): Promise<string | null> => {
  const { error: resetError } =
    await signIn.resetPasswordEmailCode.submitPassword({
      password,
      signOutOfOtherSessions: signOutOthers,
    });
  if (resetError) {
    return clerkFailureMessage(resetError);
  }
  const { error: finalError } = await signIn.finalize({
    navigate: ({ decorateUrl }) => {
      scheduleStudio(decorateUrl);
    },
  });
  return finalError ? clerkFailureMessage(finalError) : null;
};

const NewPasswordPage = ({ task = false }: { task?: boolean }) => {
  const navigate = useNavigate();
  const { isLoaded } = useAuth();
  const { fetchStatus, signIn } = useSignIn();
  const { user } = useUser();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [signOutOthers, setSignOutOthers] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const email =
    signIn.identifier ?? user?.primaryEmailAddress?.emailAddress ?? "";
  const tooShort = password.length < 8;
  const pwned = error?.includes("data breach") ?? false;

  useEffect(() => {
    if (!isLoaded || task || done || signIn.status === "needs_new_password") {
      return;
    }
    navigate(authPath("/login/reset"), { replace: true });
  }, [done, isLoaded, navigate, signIn.status, task]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (tooShort || confirm !== password) {
      return;
    }
    setError(null);
    setPending(true);
    try {
      if (signIn.status === "needs_new_password") {
        const message = await saveResetPassword({
          password,
          signIn,
          signOutOthers,
        });
        if (message) {
          setError(message);
          return;
        }
        setDone(true);
        return;
      }
      if (!user) {
        setError("Something went wrong. Try again.");
        return;
      }
      await user.updatePassword({
        newPassword: password,
        signOutOfOtherSessions: signOutOthers,
      });
      setDone(true);
      scheduleStudio();
    } catch (err) {
      const message = isClerkAPIResponseError(err)
        ? (err.errors[0]?.longMessage ?? err.errors[0]?.message)
        : null;
      setError(message ?? "Something went wrong. Try again.");
    } finally {
      setPending(false);
    }
  };

  if (done) {
    return (
      <SuccessState
        subtitle="You’re signed in. Opening Anomalos Studio…"
        title="Password updated"
      />
    );
  }

  return (
    <AuthScreen
      subtitle={
        email ? (
          <>
            For <span className="font-medium text-navy-900">{email}</span>
          </>
        ) : (
          "Choose a password you haven’t used here before."
        )
      }
      title="Choose a new password"
    >
      <form className="flex flex-col gap-8" onSubmit={onSubmit}>
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <PasswordField
              autoComplete="new-password"
              error={
                submitted && tooShort ? "Use at least 8 characters." : null
              }
              hideMessage
              id="new-password"
              label="New password"
              onChange={setPassword}
              onRevealedChange={setRevealed}
              revealed={revealed}
              value={password}
            />
            <StrengthGauge
              failedShort={submitted && tooShort}
              id="new-password-hint"
              password={password}
            />
            {pwned ? <InlineError message={error} /> : null}
          </div>
          <div className="flex flex-col gap-2">
            <PasswordField
              autoComplete="new-password"
              error={
                confirmState(password, confirm, submitted) === "mismatch"
                  ? "Passwords don’t match."
                  : null
              }
              hideMessage
              id="confirm-password"
              label="Confirm new password"
              onChange={setConfirm}
              onRevealedChange={setRevealed}
              revealed={revealed}
              value={confirm}
            />
            <ConfirmNote
              confirm={confirm}
              id="confirm-password-status"
              password={password}
              submitted={submitted}
            />
          </div>
          <label className="flex cursor-pointer items-start gap-3 text-[15px] leading-[1.45]">
            <input
              checked={signOutOthers}
              className="mt-0.5 size-5 accent-coral-700"
              onChange={(event) => setSignOutOthers(event.target.checked)}
              type="checkbox"
            />
            <span>
              <span className="block font-medium">
                Sign out of other devices
              </span>
              <span className="block text-n-600 text-sm">
                Recommended if you think someone else may have your password.
              </span>
            </span>
          </label>
          {error && !pwned ? <InlineError message={error} /> : null}
        </div>
        <PrimaryButton
          busy={pending || fetchStatus === "fetching"}
          type="submit"
        >
          Update password
        </PrimaryButton>
      </form>
    </AuthScreen>
  );
};

export default NewPasswordPage;
